import { useEffect } from 'react';

import { GeneratedReportAsset } from '@/shared/api/protocol-ts/model/dto_asset_pb';
import { AssetContent_Study_CBCT_CBCT_Metadata } from '@/shared/api/protocol-ts/model/dto_asset_study_pb';
import { ReportPrintSettings } from '@/shared/api/protocol-ts/model/dto_report_pb';
import { DICOM_MIME_TYPE } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';
import { getName } from '@/shared/lib';

import { assetsModel } from '@/entities/assets';
import { patientModel } from '@/entities/patient';
import { reportsModel } from '@/entities/reports';
import { studyModel } from '@/entities/study';
import {
  LOWER_JAW_TEETH_ISO_NUMBERS,
  toothModel,
  UPPER_JAW_TEETH_ISO_NUMBERS,
} from '@/entities/tooth';
import { userModel } from '@/entities/user';

import { getLatestDate } from '@/features/reportCards';

import { UsePreviewDocument } from '../config';
import { usePreviewFullHeadMprSlicesStatus } from '../hooks';

export const usePreviewDocument = (options: {
  reportID: string;
  settings: ReportPrintSettings | undefined;
}): UsePreviewDocument => {
  const { reportID, settings } = options;

  const fullHeadMprSlicesStatus = usePreviewFullHeadMprSlicesStatus();

  const userLocale = useAppSelector(userModel.selectors.selectUserLocale);
  const currentPatient = useAppSelector(
    patientModel.selectors.selectCurrentPatient,
  );
  const currentReport = useAppSelector(
    reportsModel.selectors.selectCurrentReport,
  );
  const report = currentReport?.ID === reportID ? currentReport : undefined;
  const patient =
    currentPatient?.ID === report?.PatientID ? currentPatient : undefined;
  const reportLoading = useAppSelector(
    reportsModel.selectors.selectDataStreamLoadingByReportID(reportID),
  );
  const teeth = useAppSelector(
    toothModel.selectors.selectTeethToShow(reportID),
  );

  const cbctStudyID = useAppSelector(
    studyModel.selectors.selectCBCTStudyIDByReportSourceStudyIDs(
      report?.SourceStudyIDs ?? [],
    ),
  );

  // TODO: 2/h discuss with the backend how to identify in the report which studies and with which IDs it was generated
  const assetByStudyID = useAppSelector(
    assetsModel.selectors.selectAssetByStudyID(report?.SourceStudyIDs[0] ?? ''),
  );

  const patientFullName = getName(
    patient?.PersonalData?.FirstName,
    patient?.PersonalData?.LastName,
    { userLocale },
  );

  const panoImage =
    assetByStudyID?.Kind.case === 'Study' &&
    assetByStudyID.Kind.value.Content.case === 'PanoramicXRay'
      ? assetByStudyID.Kind.value.Content.value.PanoramaImage
      : undefined;

  const isDICOM = panoImage?.MIME === DICOM_MIME_TYPE;

  const showPanoramaSplit = report?.Settings?.ShowPanoramaSplitInsteadOfSingle;

  const CBCTPanoImageSingle = useAppSelector(
    assetsModel.selectors.selectCBCTGPPanoImageByReportID(reportID),
  );

  const CBCTPanoImageSplit = useAppSelector(
    assetsModel.selectors.selectCBCTGPPanoImageSplit_DEPRICATED(reportID),
  );

  const CBCTPanoImage = showPanoramaSplit
    ? CBCTPanoImageSplit
    : CBCTPanoImageSingle;

  const studyMeta = useAppSelector(
    assetsModel.selectors.selectCBCTMetaDataByStudyID(cbctStudyID ?? ''),
  );

  const sourceStudies = useAppSelector(
    studyModel.selectors.selectByIDs(report?.SourceStudyIDs ?? []),
  );
  const studyDate = getLatestDate(report, sourceStudies);

  const isLoading = reportLoading === 'idle' || reportLoading === 'pending';
  const isFailed = reportLoading === 'failed';

  useEffect(() => {
    window.typewriterReadyToPDF = false;
    window.typewriterFailed = isFailed || fullHeadMprSlicesStatus === 'failed';

    if (!report || !patient) {
      // NOTE: TypewriterAbstract.waitForPageToLoad polls this flag to abort
      // incomplete captures instead of waiting for its global timeout.
      if (!isLoading) {
        window.typewriterFailed = true;
      }

      return;
    }

    if (
      isLoading ||
      isFailed ||
      !settings ||
      fullHeadMprSlicesStatus !== 'succeeded'
    ) {
      return;
    }

    // NOTE: useEffect fires after commit, not after paint. A double rAF waits for
    // the browser to actually paint this render before telling typewriter to print.
    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        window.typewriterReadyToPDF = true;
      });
    });

    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame) {
        cancelAnimationFrame(secondFrame);
      }
      window.typewriterReadyToPDF = false;
    };
  }, [isLoading, isFailed, report, patient, settings, fullHeadMprSlicesStatus]);

  const filteredTeeth = teeth.filter((tooth) => {
    const ISONumber = tooth?.Numeration?.ISO ?? 0;

    return (
      (settings?.ShowTopJaw &&
        UPPER_JAW_TEETH_ISO_NUMBERS.includes(ISONumber)) ||
      (settings?.ShowBottomJaw &&
        LOWER_JAW_TEETH_ISO_NUMBERS.includes(ISONumber))
    );
  });

  return {
    teeth: filteredTeeth,
    report,
    patient,
    CBCTPanoImage: CBCTPanoImage ?? ({} as GeneratedReportAsset),
    patientFullName,
    studyMeta: studyMeta ?? ({} as AssetContent_Study_CBCT_CBCT_Metadata),
    isLoading,
    isFailed,
    isDICOM,
    studyDate,
  };
};
